import React from 'react';
import AdvancedSEOHead from "@/components/seo/AdvancedSEOHead";
import { absoluteUrl } from '@/lib/seo';
import { Card } from '@/components/ui/card';
import { Shield, Database, Lock, FileText, AlertTriangle, CheckCircle } from 'lucide-react';

const Conformite = () => {
  const seoData = {
    title: "Conformité SP-API & RGPD — Cosmos Wave | Sécurité données Amazon",
    description: "Politique de conformité Cosmos Wave : SP-API, RGPD, sécurité données Amazon. Transparence sur l'utilisation des outils et garanties de protection.",
    breadcrumbs: [
      { name: "Accueil", url: "/" },
      { name: "Conformité", url: "/conformite" }
    ]
  };

  return (
    <>
      <AdvancedSEOHead
        title="Conformité & Conditions Générales | Cosmos Wave"
        description="Conditions générales d'utilisation, politique de confidentialité, conformité RGPD et mentions légales de Cosmos Wave, agence Amazon SPN."
        canonical={absoluteUrl("/conformite")}
        ogImage="/og/default-1200x630.jpg"
        ogType="website"
        noindex={true}
        breadcrumbs={[
          { name: "Conformité", url: "/conformite" }
        ]}
      />
      
      <div className="min-h-screen bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950">
        {/* Hero Section */}
        <section className="relative overflow-hidden pt-24 pb-16">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 via-orange-500/10 to-violet-600/20"></div>
          <div className="relative mx-auto max-w-6xl px-4">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Conformité & Sécurité
                <span className="block text-2xl md:text-4xl text-orange-400 font-normal mt-2">
                  SP-API • RGPD • Données
                </span>
              </h1>
              <p className="text-xl text-stone-300 max-w-3xl mx-auto mb-8">
                Transparence totale sur nos pratiques de sécurité, conformité SP-API Amazon, 
                protection RGPD et utilisation responsable des outils d'analyse.
              </p>
            </div>
          </div>
        </section>

        {/* SP-API Compliance */}
        <section className="py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-3xl font-bold text-white text-center mb-12">Conformité SP-API Amazon</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="p-8 bg-stone-800/50 border-stone-700">
                <div className="flex items-center gap-3 mb-6">
                  <Shield className="h-8 w-8 text-green-400" />
                  <h3 className="text-2xl font-bold text-white">Accès autorisés</h3>
                </div>
                <ul className="space-y-3">
                  {[
                    "Partenaire SP-API certifié Amazon",
                    "Accès lecture seule par défaut",
                    "Permissions granulaires configurables",
                    "Conformité Developer Policy Amazon",
                    "Audit régulier des accès"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-stone-300">
                      <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-8 bg-stone-800/50 border-stone-700">
                <div className="flex items-center gap-3 mb-6">
                  <Database className="h-8 w-8 text-blue-400" />
                  <h3 className="text-2xl font-bold text-white">Gestion données</h3>
                </div>
                <ul className="space-y-3">
                  {[
                    "Chiffrement AES-256 en transit et repos",
                    "Stockage sécurisé Europe (GDPR)",
                    "Rétention minimale nécessaire",
                    "Purge automatique données expirées",
                    "Logs d'accès complets"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-stone-300">
                      <CheckCircle className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </section>

        {/* RGPD Section */}
        <section className="py-16 bg-stone-900/50">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-3xl font-bold text-white text-center mb-12">Protection RGPD</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: <Lock className="h-8 w-8 text-orange-400" />,
                  title: "Minimisation données",
                  items: [
                    "Collecte limitée au strict nécessaire",
                    "Anonymisation quand possible",
                    "Pseudonymisation des identifiants",
                    "Durée de conservation définie"
                  ]
                },
                {
                  icon: <FileText className="h-8 w-8 text-green-400" />,
                  title: "Droits utilisateurs",
                  items: [
                    "Droit d'accès aux données",
                    "Droit de rectification",
                    "Droit à l'effacement",
                    "Portabilité des données"
                  ]
                },
                {
                  icon: <Shield className="h-8 w-8 text-blue-400" />,
                  title: "Sécurité technique",
                  items: [
                    "Chiffrement bout en bout",
                    "Accès authentifiés uniquement",
                    "Monitoring activité",
                    "Sauvegarde sécurisée"
                  ]
                }
              ].map((section, index) => (
                <Card key={index} className="p-6 bg-stone-800/50 border-stone-700">
                  <div className="text-center mb-6">{section.icon}</div>
                  <h3 className="text-xl font-bold text-white text-center mb-4">{section.title}</h3>
                  <ul className="space-y-2">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-2 text-stone-300 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Outils & Mentions */}
        <section className="py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-3xl font-bold text-white text-center mb-12">Outils & Transparence</h2>
            
            <div className="space-y-8">
              <Card className="p-8 bg-stone-800/50 border-stone-700">
                <div className="flex items-start gap-4 mb-6">
                  <AlertTriangle className="h-8 w-8 text-yellow-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-4">Mentions importantes</h3>
                    <div className="prose prose-invert max-w-none text-stone-300 space-y-4">
                      <p>
                        <strong>Absence de garanties absolues :</strong> Cosmos Wave fournit des analyses et 
                        recommandations basées sur les données disponibles et notre expertise. Les résultats 
                        Amazon dépendent de multiples facteurs externes (concurrence, saisonnalité, algorithmes) 
                        que nous ne contrôlons pas.
                      </p>
                      <p>
                        <strong>Responsabilité client :</strong> Les décisions finales d'implémentation restent 
                        sous la responsabilité du client. Nous recommandons de tester les modifications sur 
                        une sélection d'ASINs avant déploiement global.
                      </p>
                      <p>
                        <strong>Évolution des plateformes :</strong> Amazon, Google et les outils tiers évoluent 
                        régulièrement. Nos recommandations sont basées sur l'état actuel de ces plateformes 
                        et peuvent nécessiter des ajustements.
                      </p>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-8 bg-stone-800/50 border-stone-700">
                <h3 className="text-2xl font-bold text-white mb-6">Outils utilisés</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold text-orange-400 mb-3">Analytics & Données</h4>
                    <ul className="space-y-2 text-stone-300">
                      <li>• Amazon SP-API (données officielles)</li>
                      <li>• Google BigQuery (analyse de données)</li>
                      <li>• Google Analytics 4 (suivi performance)</li>
                      <li>• Outils propriétaires Cosmos Wave</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-orange-400 mb-3">Recherche & Veille</h4>
                    <ul className="space-y-2 text-stone-300">
                      <li>• Helium 10 (recherche mots-clés)</li>
                      <li>• Outils Amazon officiels (Brand Analytics)</li>
                      <li>• APIs publiques diverses</li>
                      <li>• Intelligence artificielle (analyse)</li>
                    </ul>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Contact Conformité */}
        <section className="py-16 bg-gradient-to-r from-orange-600/20 to-violet-600/20">
          <div className="mx-auto max-w-4xl px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Questions de conformité ?</h2>
            <p className="text-xl text-stone-300 mb-8">
              Pour toute question concernant notre politique de sécurité, l'utilisation de vos données 
              ou l'exercice de vos droits RGPD, contactez notre équipe.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:contact@cosmos-wave.com"
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                <FileText className="h-5 w-5" />
                Contact conformité
              </a>
              <a 
                href="mailto:dpo@cosmos-wave.com"
                className="inline-flex items-center gap-2 bg-stone-700 hover:bg-stone-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                <Shield className="h-5 w-5" />
                DPO - Données personnelles
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Conformite;